import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useFriends } from "@/context/friends-context";
import { isPlanId, planRegistry } from "@/data/meal-plan";
import type { Friend } from "@/lib/api";
import { weekPath } from "@/lib/week-paths";
import { getCurrentWeekId, getTodayDayOfWeek } from "@/lib/week-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PersonAvatar } from "@/components/person-avatar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ArrowRightIcon, UserPlusIcon, CheckIcon, XIcon, Trash2Icon } from "lucide-react";

/** The server's (English) add-friend errors, in the app's language. */
function addFriendError(err: unknown): string {
  const message = err instanceof Error ? err.message : "";
  if (message === "User not found") return "No encontramos a ese usuario.";
  if (message === "Cannot add yourself") return "No puedes agregarte a ti mismo.";
  if (message === "Already friends or request pending") return "Ya son amigos o hay una solicitud pendiente.";
  return "No se pudo enviar la solicitud. Intenta de nuevo.";
}

export function FriendsManager({ onClose }: { onClose?: () => void } = {}) {
  const { friends, loading, loadError, refresh, addFriend, acceptFriend, deleteFriend } = useFriends();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** The friendship whose accept / reject is in flight (its buttons are disabled meanwhile). */
  const [busyId, setBusyId] = useState<number | null>(null);

  // Requests and acceptances from the other side show up when Amigos opens.
  useEffect(() => {
    void refresh();
  }, [refresh]);
  // Kept after closing so the dialog doesn't lose its title while animating out.
  const [removing, setRemoving] = useState<Friend | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const accepted = friends.filter(f => f.status === "accepted");
  const pendingReceived = friends.filter(f => f.status === "pending" && f.direction === "received");
  const pendingSent = friends.filter(f => f.status === "pending" && f.direction === "sent");

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!username.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      await addFriend(username.trim());
      setUsername("");
    } catch (err) {
      setError(addFriendError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function respond(friend: Friend, accept: boolean) {
    setError(null);
    setBusyId(friend.id);
    try {
      await (accept ? acceptFriend(friend.id) : deleteFriend(friend.id));
    } catch {
      setError(
        accept
          ? `No se pudo aceptar a ${friend.username}. Intenta de nuevo.`
          : `No se pudo rechazar a ${friend.username}. Intenta de nuevo.`,
      );
    } finally {
      setBusyId(null);
    }
  }

  /** Friend mode on their current week, today's day. */
  function planFor(friend: Friend) {
    const weekId = getCurrentWeekId();
    navigate(weekPath(friend.friend_id, weekId, getTodayDayOfWeek(weekId) ?? "mon"));
  }

  function askRemove(friend: Friend) {
    setRemoving(friend);
    setConfirmOpen(true);
  }

  async function confirmRemove() {
    setConfirmOpen(false);
    if (!removing) return;
    setError(null);
    try {
      await deleteFriend(removing.id);
    } catch {
      setError(`No se pudo eliminar a ${removing.username}. Intenta de nuevo.`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Amigos</CardTitle>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose} title="Cerrar" aria-label="Cerrar" className="-my-2 size-11">
            <XIcon className="size-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add friend form */}
        <form onSubmit={handleAdd} className="flex gap-2">
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Nombre de usuario"
            aria-label="Nombre de usuario"
            className="h-11 flex-1"
          />
          <Button type="submit" className="h-11" disabled={submitting || !username.trim()}>
            <UserPlusIcon className="size-4" />
            Agregar
          </Button>
        </form>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        {loadError && (
          <p role="alert" className="text-sm text-muted-foreground">
            {loading ? "No se pudo cargar tu lista de amigos." : "No se pudo actualizar tu lista de amigos."}{" "}
            <button
              type="button"
              onClick={() => void refresh()}
              className="-my-2 inline-flex min-h-11 items-center font-medium text-foreground underline underline-offset-2"
            >
              Reintentar
            </button>
          </p>
        )}

        {/* Pending received requests */}
        {pendingReceived.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Solicitudes recibidas</h3>
            <div className="space-y-1">
              {pendingReceived.map(f => (
                <div key={f.id} className="flex items-center justify-between py-0.5 pl-3 pr-1 rounded bg-amber-50 text-sm">
                  <span className="min-w-0 truncate font-medium">{f.username}</span>
                  <div className="flex shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-11"
                      disabled={busyId === f.id}
                      onClick={() => void respond(f, true)}
                      title="Aceptar"
                      aria-label={`Aceptar a ${f.username}`}
                    >
                      <CheckIcon className="size-4 text-green-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-11"
                      disabled={busyId === f.id}
                      onClick={() => void respond(f, false)}
                      title="Rechazar"
                      aria-label={`Rechazar a ${f.username}`}
                    >
                      <XIcon className="size-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Al aceptar, ambos podrán planificar las comidas del otro.
            </p>
          </div>
        )}

        {/* Pending sent requests */}
        {pendingSent.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Solicitudes enviadas</h3>
            <div className="space-y-1">
              {pendingSent.map(f => (
                <div key={f.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-muted/50 text-sm">
                  <span>{f.username}</span>
                  <span className="text-xs text-muted-foreground">Pendiente</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Accepted friends */}
        {accepted.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-2">Amigos</h3>
            <ul className="divide-y divide-border">
              {accepted.map(f => (
                <AcceptedFriendRow
                  key={f.id}
                  friend={f}
                  onPlan={() => planFor(f)}
                  onRemove={() => askRemove(f)}
                />
              ))}
            </ul>
          </div>
        )}

        {!loading && accepted.length === 0 && pendingReceived.length === 0 && pendingSent.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">
            No tienes amigos agregados. Agrega a alguien por su nombre de usuario.
          </p>
        )}

        <p className="text-xs text-muted-foreground">
          Tus amigos pueden ver y planificar tus comidas, y tú las suyas.
        </p>
      </CardContent>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`¿Eliminar a ${removing?.username ?? ""}?`}
        description="Dejarán de ver sus semanas y ninguno podrá planificar las comidas del otro."
        confirmText="Eliminar"
        destructive
        onConfirm={() => void confirmRemove()}
      />
    </Card>
  );
}

/**
 * Name over plan name (the plan name wraps if it must), then the two
 * actions side by side, 44 px targets. The trash sits at the edge, its target reaching into
 * the card padding so its icon lines up with the content edge.
 */
function AcceptedFriendRow({
  friend,
  onPlan,
  onRemove,
}: {
  friend: Friend;
  onPlan: () => void;
  onRemove: () => void;
}) {
  // Registry names already read "Plan David", "Plan Romina Nutri 2"…
  const planName = isPlanId(friend.plan_id) ? planRegistry[friend.plan_id].name : null;
  return (
    <li className="flex items-center gap-3 py-1.5">
      <PersonAvatar name={friend.username} tone="friend" />
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-sm font-medium">{friend.username}</p>
        {planName && (
          // Wraps rather than truncates: "Plan Romina Nutri 2" needs two lines at 375 px.
          <p className="text-xs break-words text-muted-foreground">{planName}</p>
        )}
      </div>
      <div className="-mr-3 flex shrink-0 items-center gap-1">
        <Button
          variant="outline"
          className="h-11 px-3"
          onClick={onPlan}
          aria-label={`Planificar las comidas de ${friend.username}`}
        >
          Planificar
          <ArrowRightIcon className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-11"
          onClick={onRemove}
          title="Eliminar"
          aria-label={`Eliminar a ${friend.username}`}
        >
          <Trash2Icon className="size-4 text-muted-foreground" />
        </Button>
      </div>
    </li>
  );
}
